import { useEffect, useState } from "react";
import { Phone, Mail, MapPin, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/public/Bits";

export default function Contact() {
  const [info, setInfo] = useState({ phone: "9654712772", email: "ukf.org.in@gmail.com", address: "", extra: "" });
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/public/settings").then((r) => { if (r.data.contact) setInfo(r.data.contact); }).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/public/contact", form);
      toast.success("Message sent. Thank you for contacting UKF.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const field = "w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm outline-none transition focus:border-ukf-red focus:ring-2 focus:ring-ukf-red/20";

  return (
    <div>
      <PageHeader eyebrow="Get in touch" title="Contact" subtitle="Contact UKF using the details below." kanji="連合" />
      <div className="mx-auto grid max-w-7xl gap-10 px-4 py-14 sm:px-6 md:grid-cols-2 lg:px-8">
        <div className="space-y-4">
          <a href={`tel:${info.phone}`} data-testid="contact-phone"
            className="flex items-center gap-4 rounded-lg border border-slate-200 bg-white p-5 transition-all hover:border-ukf-gold hover:shadow-md">
            <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-ukf-red/10 text-ukf-red"><Phone className="h-5 w-5" /></span>
            <div><p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Phone</p><p className="font-medium text-ukf-ink">{info.phone}</p></div>
          </a>
          <a href={`mailto:${info.email}`} data-testid="contact-email"
            className="flex items-center gap-4 rounded-lg border border-slate-200 bg-white p-5 transition-all hover:border-ukf-gold hover:shadow-md">
            <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-ukf-red/10 text-ukf-red"><Mail className="h-5 w-5" /></span>
            <div><p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Email</p><p className="font-medium text-ukf-ink break-all">{info.email}</p></div>
          </a>
          {info.address && (
            <div className="flex items-center gap-4 rounded-lg border border-slate-200 bg-white p-5">
              <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-ukf-red/10 text-ukf-red"><MapPin className="h-5 w-5" /></span>
              <div><p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Address</p><p className="font-medium text-ukf-ink whitespace-pre-line">{info.address}</p></div>
            </div>
          )}
          {info.extra && <p className="whitespace-pre-line rounded-lg bg-white p-5 text-sm text-slate-600 border border-slate-200">{info.extra}</p>}
        </div>

        <form onSubmit={submit} data-testid="contact-form" className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="font-serif text-2xl font-semibold text-ukf-ink">Send a message</h2>
          <div className="mt-6 space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700" htmlFor="c-name">Name</label>
              <input id="c-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name-input" className={field} />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700" htmlFor="c-email">Email</label>
              <input id="c-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email-input" className={field} />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700" htmlFor="c-subject">Subject</label>
              <input id="c-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="contact-subject-input" className={field} />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700" htmlFor="c-message">Message</label>
              <textarea id="c-message" required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message-input" className={field} />
            </div>
            <button type="submit" disabled={sending} data-testid="contact-submit-button"
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-ukf-red px-6 py-3 text-sm font-semibold text-white transition-all hover:bg-ukf-red-dark active:scale-[0.98] disabled:opacity-60">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {sending ? "Sending…" : "Send message"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
